import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ShieldCheck, Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const success = await login(email, password);
    
    if (!success) {
      setError('Invalid credentials. Please try again.');
    }
    
    setIsLoading(false);
  };

  const handleDemoAccountClick = (accountEmail: string) => {
    setEmail(accountEmail);
    setPassword('password');
    setError('');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 py-8 px-4">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-midnight-indigo p-3 rounded-2xl credential-capital-shadow-lg">
              <ShieldCheck className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-midnight-indigo mb-2">
            Credential Capital
          </h1>
          <p className="text-warm-slate text-lg">
            Secure Digital Credentialing Platform
          </p>
        </div>

        {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    
          <div className="lg:col-span-2"> */}
          <div className="text-center">
          <div className="flex justify-center mb-4">
            <Card className="credential-capital-card h-fit" style={{width:'500px'}}>
              <CardHeader className="text-center pb-3">
                <CardTitle className="text-xl font-display text-charcoal-ink">
                  Welcome Back
                </CardTitle>
                <CardDescription className="text-warm-slate text-sm">
                  Sign in to access your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-charcoal-ink font-medium text-sm">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="credential-capital-input h-10"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-charcoal-ink font-medium text-sm">
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="credential-capital-input h-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warm-slate hover:text-charcoal-ink transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-end mb-4">
                    <button
                      type="button"
                      onClick={() => navigate('/request-reset')}
                      className="text-sm text-warm-slate hover:text-midnight-indigo transition-colors"
                    >
                      Forgot Password or Resend Invite Link
                    </button>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-10 credential-capital-button-primary text-sm font-medium"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Signing in...
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                {/* Quick Login Instructions */}
                {/* <div className="pt-4 border-t border-gray-200 space-y-2">
                  <p className="text-xs text-warm-slate text-center">
                    💡 Click any demo account, then sign in
                  </p>
                  <p className="text-xs text-warm-slate text-center">
                    Password: <span className="font-mono font-medium bg-cool-gray-100 px-1.5 py-0.5 rounded text-charcoal-ink">password</span>
                  </p>
                </div> */}
              </CardContent>
            </Card>
          </div>

        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-warm-slate">
            Powered by Credential Capital • Secure • Verifiable • Trusted
          </p>
        </div>
      </div>
    </div>
  );
}